import { useMutation, useQueryClient } from '@tanstack/react-query';
import { WaitListEntry } from 'src/models/appointment/_types';
import { deleteWaitListEntry } from 'src/models/appointment/appointment';
import { Visit, VisitStatus } from 'src/models/visit/_types';
import { updateVisit } from 'src/models/visit/visit';
import toast from 'src/utils/toast';

export const useAppointmentMembersSection = () => {
  const queryClient = useQueryClient();

  const updateVisitMutation = useMutation({
    mutationFn: (visit: Visit) => {
      return updateVisit(visit);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Visit(s) successfully updated');
    },
    onError: () => {
      toast.error('Visit could not be updated');
    },
  });

  const bulkUpdateVisitMutation = useMutation({
    mutationFn: (visit: Visit) => {
      return updateVisit(visit);
    },
  });

  const updateVisitStatus = (
    visit: Visit,
    status: VisitStatus,
    additionalParams?: {
      chargeNoshow?: boolean;
      chargeCancellation?: boolean;
      paidByPackageId?: boolean;
    }
  ) => {
    updateVisitMutation.mutate({ ...visit, status, ...additionalParams });
  };
  const deleteWaitListMutation = useMutation({
    mutationFn: (entry: WaitListEntry) => {
      return deleteWaitListEntry({ id: entry.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Successfully removed from waitlist');
    },
    onError: () => {
      toast.error('Unable to remove from waitlist');
    },
  });

  const deleteWaitlistEntry = (entry: WaitListEntry) => {
    deleteWaitListMutation.mutate(entry);
  };

  const bulkUpdateVisits = async (visits: Visit[], params) => {
    const visitPromises = visits.map((visit) => {
      return bulkUpdateVisitMutation.mutateAsync({ ...visit, ...params });
    });

    await Promise.all(visitPromises);
    queryClient.invalidateQueries({ queryKey: ['appointments'] });
    toast.success('Visit(s) successfully updated');
  };

  return {
    updateVisitStatus,
    bulkUpdateVisits,
    deleteWaitlistEntry,
    isUpdatingVisit: updateVisitMutation.isPending,
  };
};
